<script lang="ts" setup>
import type {
  AcceptableValue,
  ComboboxRootProps
} from 'reka-ui'
import type { ButtonColors, ButtonShades, ButtonSizes, ButtonVariants } from '~/components/ui/Button.vue'
import { computed } from 'vue'
import { tv } from 'tailwind-variants'
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxViewport,
  useForwardPropsEmits
} from 'reka-ui'
import { faCheck, faChevronDown } from '@fortawesome/duotone-regular-svg-icons'
import { getBaseColorName } from '~/utils/colors'
import { buttonTheme } from '~/components/ui/variants/buttonTheme'

export interface AuComboboxProps extends ComboboxRootProps {
  options: AcceptableValue[]
  optionId: (option: any) => string | number
  optionLabel?: (option: any) => string
  noOptions?: string
  placeholder?: string
  color?: ButtonColors | string
  variant?: ButtonVariants
  size?: ButtonSizes
  shade?: ButtonShades
}

const props = withDefaults(defineProps<AuComboboxProps>(), {
  placeholder: 'Søk...',
  noOptions: 'Ingen resultater',
  shade: 'dark',
})

const emits = defineEmits<{
  (e: 'update:modelValue', value: AcceptableValue[]): void
  (e: 'search', value: string): void
}>()

const forwarded = useForwardPropsEmits(props, emits)

const normalizedColor = computed((): ButtonColors | undefined => {
  const base = props.color && getBaseColorName(props.color as string)
  return (base || undefined) as ButtonColors | undefined
})

const selectItem = tv({
  extend: buttonTheme,
  base: 'flex w-full focus-visible:ring-0'
})

const backgroundTheme = tv({
  base: '',
  variants: {
    color: {
      brand: '',
      gray: '',
      yellow: '',
      orange: '',
      coral: '',
      violet: '',
      purple: '',
      blue: '',
      seagreen: '',
      green: '',
      white: '',
      teacher: '',
    },
  },
  compoundVariants: [
    { color: 'gray', class: 'bg-gray-5' },
    { color: ['orange', 'yellow', 'teacher'], class: 'bg-orange-5' },
    { color: 'coral', class: 'bg-coral-5' },
    { color: 'violet', class: 'bg-violet-5' },
    { color: 'purple', class: 'bg-purple-5' },
    { color: 'blue', class: 'bg-blue-5' },
    { color: 'seagreen', class: 'bg-seagreen-5' },
    { color: ['green', 'brand'], class: 'bg-green-5' },
    { color: 'white', class: 'bg-white' },
  ],
  defaultVariants: {
    color: 'white',
  }
})
</script>

<template>
  <ComboboxRoot v-bind="forwarded">
    <ComboboxAnchor
      class="rounded-xl relative flex transition-all focus-within:ring"
      :class="{
        'bg-gray-10': shade === 'dark',
        'hover:not-disabled:outline-green-10 outline-green-5 text-green-5 hover:not-disabled:bg-green-5/10 focus:bg-green-5/10 bg-transparent outline -outline-offset-1': shade === 'light',
      }"
    >
      <ComboboxInput
        :placeholder
        class="w-full p-3"
        :class="{
          'placeholder:text-gray-40': shade === 'dark',
          'placeholder:text-green-10': shade === 'light',
        }"
        @input="(e: any) => emits('search', e.target.value)"
      />
      <ComboboxTrigger class="absolute top-1/2 right-3 -translate-y-1/2">
        <font-awesome-icon
          :icon="faChevronDown"
          class="size-4"
        />
      </ComboboxTrigger>
    </ComboboxAnchor>

    <ComboboxPortal>
      <ComboboxContent
        :body-lock="false"
        :class="backgroundTheme({ color: normalizedColor })"
        :side-offset="8"
        class="data-[state=open]:animate-select-in data-[state=closed]:animate-select-out z-[1000] flex max-h-(--reka-combobox-content-available-height) w-full min-w-(--reka-combobox-trigger-width) origin-(--reka-combobox-content-transform-origin) flex-col items-center rounded-xl p-2 shadow-lg transition-transform"
        position="popper"
      >
        <ComboboxViewport class="w-full">
          <template v-if="options.length">
            <ComboboxItem
              v-for="option in options"
              :key="optionId(option)"
              :class="selectItem({ color: normalizedColor, variant: 'ghost', size })"
              :value="optionId(option)"
            >
              {{ optionLabel ? optionLabel(option) : option }}
              <ComboboxItemIndicator>
                <font-awesome-icon :icon="faCheck" />
              </ComboboxItemIndicator>
            </ComboboxItem>
          </template>
          <template v-else>
            {{ noOptions }}
          </template>
        </ComboboxViewport>
      </ComboboxContent>
    </ComboboxPortal>
  </ComboboxRoot>
</template>
